import React from 'react';
import useTranslatedArray from 'hooks/useTranslatedArray';
import useTranslatedRecord from 'hooks/useTranslatedRecord';
import { readString } from 'hooks/lib/translationGuards';
import { TFunction } from 'types/i18n';
import isValidFaqQuestion from '../lib/isValidFaqQuestion';
import { QuestionData } from '../FaqQuestion';

interface FaqTranslation {
  title: string;
  subtitle: string;
  questions: QuestionData[];
}

interface UseFaqTranslation {
  (translationKey: string, t: TFunction): FaqTranslation;
}

const useFaqTranslation: UseFaqTranslation = (translationKey, t) => {
  // The defensive `Array.isArray` guard for `questions` lives in the hook so
  // every consumer gets it for free (ESCR-1618 follow-up). Letting the
  // default `onInvalid` fire keeps logging behavior consistent across all
  // three consumers (`HelpfulToKnow`, `SsnVerificationWelcomePage`, `Faq`).
  const rawQuestions = useTranslatedArray<QuestionData>(translationKey, t, {
    subPath: 'questions',
  });
  // Title and subtitle are computed synchronously alongside `questions` so
  // they all populate in the same render. With a `useEffect` + `useState`
  // pattern, `questions` (sync) and `title`/`subtitle` (async) would land
  // on different render passes, briefly painting the FAQ with the fallback
  // title before the real one arrived.
  const translationRoot = useTranslatedRecord(translationKey, t);

  const questions = React.useMemo(
    () => rawQuestions.filter(isValidFaqQuestion),
    [rawQuestions],
  );

  const { title, subtitle } = React.useMemo(
    () => ({
      title: readString(translationRoot, 'title'),
      subtitle: readString(translationRoot, 'subtitle'),
    }),
    [translationRoot],
  );

  return {
    questions,
    title,
    subtitle,
  };
};

export default useFaqTranslation;
